import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { TextInput, FormDataConsumer, useNotify, useRefresh } from "react-admin";
import { useForm } from "react-final-form";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { apiUrl } from "../../dataProvider";
import CustomAdminSelect from "../BestSellers/CustomAdminSelect";

export const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

// Omit the query param entirely when there's no platform selected, rather
// than sending it as an empty string, so the backend treats it as null.
const buildBuilderPlatformQuery = (builderPlatformId) =>
  isEmptyValue(builderPlatformId) ? "" : `?builder_platform_id=${builderPlatformId}`;

const useStyles = makeStyles(() => ({
  heading: {
    fontWeight: "bold",
    marginTop: "10px",
    borderBottom: "3px solid #a6a5a5ff",
    paddingBottom: "5px",
  },
  disclaimerDeleteCell: {
    display: "flex",
    alignItems: "center",
    paddingTop: 8,
  },
  disclaimerInfoCard: {
    padding: "12px 16px",
    marginBottom: 8,
    backgroundColor: "#f5f7fa",
    border: "1px solid #e0e4e8",
    borderLeft: "4px solid #1976d2",
    borderRadius: 4,
  },
  disclaimerInfoTitle: {
    fontWeight: 600,
    marginBottom: 8,
    color: "#1a1a1a",
  },
  disclaimerInfoList: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px 24px",
    margin: "0 0 0 12px",
    paddingLeft: 0,
    "& li": {
      margin: 0,
      maxWidth: "100%",
      wordBreak: "break-word",
    },
  },
  disclaimerHint: {
    marginBottom: 4,
  },
}));

// Watches the builder platform dropdown and re-fetches this material's
// disclaimer (via the show API's builder_platform_id query param) whenever
// the selection changes, including back to "cleared".
const DisclaimerDataSync = ({ materialId, builderPlatformId }) => {
  const form = useForm();
  const notify = useNotify();
  // Normalize so the initial "undefined" (before the record has loaded)
  // isn't treated as a change once it settles to the record's actual
  // (possibly null) value — that would trigger an unwanted fetch on mount.
  const normalize = (value) => (isEmptyValue(value) ? null : value);
  const previousBuilderPlatformIdRef = useRef(normalize(builderPlatformId));

  useEffect(() => {
    const normalizedBuilderPlatformId = normalize(builderPlatformId);
    const previousBuilderPlatformId = previousBuilderPlatformIdRef.current;
    previousBuilderPlatformIdRef.current = normalizedBuilderPlatformId;

    if (normalizedBuilderPlatformId === previousBuilderPlatformId) {
      return;
    }

    if (isEmptyValue(builderPlatformId)) {
      form.change("disclaimers.builder_platform_id", null);
    }

    const fetchDisclaimerData = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/material-data/${materialId}${buildBuilderPlatformQuery(builderPlatformId)}`,
        );
        const disclaimers = response?.data?.data?.disclaimers;
        form.change("disclaimers", {
          builder_platform_id: isEmptyValue(builderPlatformId) ? null : builderPlatformId,
          disclaimer: disclaimers?.disclaimer || "",
        });
      } catch (error) {
        notify(error?.response?.data?.message || "Failed to fetch disclaimer data", { type: "warning" });
      }
    };

    fetchDisclaimerData();
  }, [builderPlatformId, materialId, form, notify]);

  return null;
};

DisclaimerDataSync.propTypes = {
  materialId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  builderPlatformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const DisclaimerDeleteButton = ({ materialId, builderPlatformId }) => {
  const form = useForm();
  const notify = useNotify();
  const refresh = useRefresh();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(
        `${apiUrl}/material-data/${materialId}/disclaimer${buildBuilderPlatformQuery(builderPlatformId)}`,
      );
      notify("Disclaimer removed", { type: "success" });
      form.change("disclaimers.disclaimer", "");
      refresh();
    } catch (error) {
      notify(error?.response?.data?.message || "Failed to remove disclaimer", { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.disclaimerDeleteCell}>
      <IconButton aria-label="Remove disclaimer" onClick={handleClick} disabled={loading} size="small">
        {loading ? <CircularProgress size={20} /> : <DeleteIcon />}
      </IconButton>
    </div>
  );
};

DisclaimerDeleteButton.propTypes = {
  materialId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  builderPlatformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const DisclaimersSection = ({ record }) => {
  const classes = useStyles();
  const materialId = record?.id;

  return (
    <Grid container spacing={2} alignItems="center" justify="space-between">
      <Grid item container xs={12} className={classes.heading}>
        Disclaimers
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary" className={classes.disclaimerHint}>
          Leave the builder platform unselected to set a default disclaimer that applies to every store.
          Select a specific builder platform to set a disclaimer just for that store, overriding the default.
        </Typography>
      </Grid>
      {Array.isArray(record?.builder_platforms) && record.builder_platforms.length > 0 ? (
        <Grid item xs={12}>
          <Paper className={classes.disclaimerInfoCard} elevation={0}>
            <Typography variant="subtitle2" component="h3" className={classes.disclaimerInfoTitle}>
              Platforms with custom disclaimers
            </Typography>
            <Typography variant="body2" component="div" color="textSecondary">
              <ul className={classes.disclaimerInfoList}>
                {record.builder_platforms.map((label, index) => (
                  <li key={`${label}-${index}`}>{label}</li>
                ))}
              </ul>
            </Typography>
          </Paper>
        </Grid>
      ) : null}
      <Grid item xs={3}>
        <CustomAdminSelect
          label="Builder Platform"
          source="disclaimers.builder_platform_id"
          resource="builder-platform?page[number]=1&page[size]=1000"
          optionLabel="store_name"
        />
      </Grid>
      <FormDataConsumer>
        {({ formData }) => {
          const builderPlatformId = formData?.disclaimers?.builder_platform_id;
          const hasDisclaimerText = !isEmptyValue(formData?.disclaimers?.disclaimer);
          const showDeleteButton = Boolean(materialId) && hasDisclaimerText;

          return (
            <>
              {materialId ? (
                <DisclaimerDataSync materialId={materialId} builderPlatformId={builderPlatformId} />
              ) : null}
              <Grid item xs={showDeleteButton ? 7 : 8}>
                <TextInput source="disclaimers.disclaimer" label="Disclaimer" multiline fullWidth />
              </Grid>
              {showDeleteButton ? (
                <Grid item xs={1}>
                  <DisclaimerDeleteButton materialId={materialId} builderPlatformId={builderPlatformId} />
                </Grid>
              ) : null}
            </>
          );
        }}
      </FormDataConsumer>
    </Grid>
  );
};

DisclaimersSection.propTypes = {
  record: PropTypes.object,
};

export default DisclaimersSection;
